import { useEffect, useMemo, useRef } from "react";
import {
  IconAlertCircle,
  IconArrowUp,
  IconCheck,
  IconLoader2,
  IconRefresh,
  IconSparkles,
} from "@tabler/icons-react";
import {
  IMAGE2_REVISION_INSTRUCTION_MAX_LENGTH,
  image2RevisionInstructionLength,
} from "./image2-revision-instruction";
import {
  image2RevisionJobMessage,
  latestRollbackVersionBySlide,
  useImage2RevisionSession,
  visibleImage2RevisionMessages,
  type Image2RevisionSession,
} from "./image2-revision-session";
import { userFacingGenerationMessage } from "./user-facing-generation-message";
import type { ProjectDetailResponse, ProjectRender } from "./project-types";

export interface ExistingPptChatSlide {
  index: number;
  title: string;
  status: "waiting" | "running" | "succeeded" | "failed" | "cancelled";
  imageUrl?: string;
  thumbnailUrl?: string;
  progressText?: string;
  errorMessage?: string;
}

export interface ExistingPptGenerationMessage {
  id: string;
  tone: "info" | "progress" | "success" | "error";
  text: string;
}

export type ImageModeChatVariant = "image_generation" | "existing_ppt_beautify";
export { editableImage2RenderId } from "./image2-revision-session";

interface ImageModeChatProps {
  variant: ImageModeChatVariant;
  detail: ProjectDetailResponse;
  render?: ProjectRender;
  slides: ExistingPptChatSlide[];
  selectedIndex: number;
  phase: "planning" | "awaiting_credits" | "queued" | "rendering" | "complete" | "failed" | "cancelled";
  percent: number;
  externalError?: string;
  revisionSession: Image2RevisionSession;
}

export function ExistingPptBeautifyChat({
  detail,
  render,
  slides,
  selectedIndex,
  phase,
  percent,
  externalError,
  onRefresh,
}: {
  detail: ProjectDetailResponse;
  render?: ProjectRender;
  slides: ExistingPptChatSlide[];
  selectedIndex: number;
  phase: "planning" | "awaiting_credits" | "queued" | "rendering" | "complete" | "failed" | "cancelled";
  percent: number;
  externalError?: string;
  onRefresh: () => Promise<void>;
}) {
  const selectedPage = slides[selectedIndex]?.index || selectedIndex + 1;
  const revisionSession = useImage2RevisionSession({
    render,
    selectedPage,
    enabled: phase === "complete" && Boolean(slides[selectedIndex]?.imageUrl || slides[0]?.imageUrl),
    idempotencyPrefix: "existing-ppt",
    onRefresh,
  });
  return <ImageModeChat
    variant="existing_ppt_beautify"
    detail={detail}
    render={render}
    slides={slides}
    selectedIndex={selectedIndex}
    phase={phase}
    percent={percent}
    externalError={externalError}
    revisionSession={revisionSession}
  />;
}

export function ImageModeChat({
  variant,
  detail,
  slides,
  selectedIndex,
  phase,
  percent,
  externalError,
  revisionSession,
}: ImageModeChatProps) {
  const existingPpt = variant === "existing_ppt_beautify";
  const selectedSlide = slides[selectedIndex] || slides[0];
  const selectedPage = selectedSlide?.index || selectedIndex + 1;
  const historyRef = useRef<HTMLDivElement>(null);
  const {
    messages,
    pageMessages,
    draft,
    regions,
    job: activeJob,
    feedback,
    processing,
    canEdit,
    instructionValid,
    setDraft,
    submit,
    rollback,
  } = revisionSession;

  const visibleMessages = useMemo(
    () => visibleImage2RevisionMessages(messages),
    [messages],
  );
  const persistentPending = pageMessages.some((message) => message.messageType === "revision_progress");
  const generationMessages = useMemo(
    () => buildImageModeGenerationMessages({ variant, detail, slides, phase, percent }),
    [variant, detail, slides, phase, percent],
  );
  const inlineError = feedback?.tone === "error" ? feedback.text : externalError;
  const showInlineError = Boolean(
    inlineError
    && !generationMessages.some((message) => message.tone === "error" && message.text === inlineError),
  );

  useEffect(() => {
    const node = historyRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [activeJob, feedback, generationMessages, selectedPage, visibleMessages]);

  const latestVersionBySlide = latestRollbackVersionBySlide(messages);
  return <section className={`existing-ppt-chat image-mode-chat ${existingPpt ? "advanced-beautify-page advanced-beautify-chat" : ""} variant-${variant}`} aria-label={existingPpt ? "美化现有 PPT 对话框" : "图片模式生成对话框"}>
    <header className="existing-ppt-chat-head">
      <div className="existing-ppt-chat-identity">
        <span className="existing-ppt-chat-avatar"><IconSparkles size={18}/></span>
        <span>
          <strong>{existingPpt ? "AI 美化助手" : "AI 图片助手"}</strong>
          <small>SYSTEM READY</small>
        </span>
      </div>
      <p>{phase === "complete"
        ? regions.length ? `已框选 ${regions.length} 个区域，请逐行填写要求` : "发送消息即可修改或重新生成本页"
        : `整体生成进度 ${percent}%`}</p>
    </header>
    <div className="existing-ppt-chat-history" ref={historyRef} aria-live="polite">
      {visibleMessages.map((message) => {
        const historical = ["initial_version", "revision", "rollback"].includes(message.messageType) && Boolean(message.resource);
        const expired = historical && (message.resourceExpired || message.resourceDeleted);
        const showRollback = message.canRollback && (
          message.messageType === "initial_version"
          || (message.resource?.slideIndex && latestVersionBySlide.get(message.resource.slideIndex) === message.id)
        );
        return <article key={message.id} className={`existing-ppt-chat-message ${message.role} ${message.messageType}`}>
          <div>
            <span>{message.role === "user" ? "你" : "AI"}</span>
            {message.resource?.slideIndex && <small>第 {message.resource.slideIndex} 页</small>}
            <time>{formatMessageTime(message.createdAt)}</time>
            {message.messageType === "revision_progress" && <IconLoader2 size={14}/>}
          </div>
          <p>{message.messageType === "revision_progress" ? trimTerminalPunctuation(userFacingGenerationMessage(message.contentText)) : userFacingGenerationMessage(message.contentText)}{message.messageType === "revision_progress" && <AnimatedEllipsis/>}</p>
          {historical && <footer>
            <span>{expired ? "历史资源已过期，消息仍会保留" : `版本保留至 ${formatMessageTime(message.expiresAt)}`}</span>
            {showRollback && <button type="button" disabled={processing} onClick={() => void rollback(message)}>
              <IconRefresh size={13}/>{message.messageType === "initial_version" ? "恢复首次版本" : "回退"}
            </button>}
          </footer>}
        </article>;
      })}
      {generationMessages.map((message) => <article key={message.id} className={`existing-ppt-chat-message assistant ${message.tone}`}>
        <div><span>AI</span>{message.tone === "progress" ? <IconLoader2 size={14}/> : message.tone === "success" ? <IconCheck size={14}/> : message.tone === "error" ? <IconAlertCircle size={14}/> : null}</div>
        <p>{message.tone === "progress" ? trimTerminalPunctuation(userFacingGenerationMessage(message.text)) : userFacingGenerationMessage(message.text)}{message.tone === "progress" && <AnimatedEllipsis/>}</p>
        {message.tone === "progress" && <div className="existing-ppt-generation-meter" role="progressbar" aria-label={`整体生成进度 ${percent}%`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
          <span><i style={{ width: `${percent}%` }}/></span>
          <small><b>{percent}%</b><em>{slides.filter((slide) => slide.status === "succeeded").length}/{Math.max(1, slides.length)} 页已完成</em></small>
        </div>}
      </article>)}
      {activeJob && !persistentPending && <article className="existing-ppt-chat-message assistant revision_progress" role="status">
        <div><span>AI</span><small>第 {selectedPage} 页</small><IconLoader2 size={14}/></div>
        <p>{image2RevisionJobMessage(activeJob.status, selectedPage)}<AnimatedEllipsis/></p>
      </article>}
      {feedback?.tone === "success" && <article className="existing-ppt-chat-message assistant success" role="status">
        <div><span>AI</span><IconCheck size={14}/></div>
        <p>{userFacingGenerationMessage(feedback.text)}</p>
      </article>}
      {showInlineError && <article className="existing-ppt-chat-message assistant error terminal-error" role="alert">
        <div><span>系统</span><IconAlertCircle size={14}/></div>
        <p>{userFacingGenerationMessage(inlineError)}</p>
      </article>}
    </div>
    <div className="ppt-chat-compose-card">
      <label className="existing-ppt-chat-input" htmlFor="image-mode-chat-instruction">
        <span className="ppt-chat-compose-context"><b>当前编辑</b><strong>第 {selectedPage} 页</strong></span>
        <textarea
          id="image-mode-chat-instruction"
          value={draft}
          disabled={!canEdit}
          maxLength={IMAGE2_REVISION_INSTRUCTION_MAX_LENGTH}
          aria-describedby="image-mode-chat-composer-hint"
          placeholder={processing
            ? `第 ${selectedPage} 页正在处理中…`
            : phase !== "complete"
              ? "整套 PPT 生成完成后可发送修改要求"
              : regions.length
                ? "按“框X：修改要求”逐行填写"
              : existingPpt
                ? "描述你希望这页如何变化，例如：突出标题并放大右侧图表"
                : "描述重新生成要求，例如：换成深色科技风，突出核心数据并减少文字"}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (regions.length === 0 && event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void submit();
            }
          }}
        />
      </label>
      <footer className="existing-ppt-chat-composer">
        <span className="ppt-chat-compose-count">{image2RevisionInstructionLength(draft)} / {IMAGE2_REVISION_INSTRUCTION_MAX_LENGTH}</span>
        <small id="image-mode-chat-composer-hint" className="ppt-chat-compose-hint">
          {regions.length ? "每个框选区域单独一行" : "Enter 发送 · Shift+Enter 换行"}
        </small>
        <button type="button" disabled={!canEdit || (regions.length === 0 && !instructionValid)} onClick={() => void submit()} aria-label={`发送第 ${selectedPage} 页修改要求`} title="发送修改要求">
          {processing ? <IconLoader2 size={19}/> : <IconArrowUp size={19}/>}
        </button>
      </footer>
    </div>
  </section>;
}

export function buildExistingPptGenerationMessages({
  detail,
  slides,
  phase,
  percent,
}: {
  detail: Pick<ProjectDetailResponse, "task">;
  slides: ExistingPptChatSlide[];
  phase: "planning" | "awaiting_credits" | "queued" | "rendering" | "complete" | "failed" | "cancelled";
  percent: number;
}): ExistingPptGenerationMessage[] {
  return buildImageModeGenerationMessages({
    variant: "existing_ppt_beautify",
    detail,
    slides,
    phase,
    percent,
  });
}

export function buildImageModeGenerationMessages({
  variant,
  detail,
  slides,
  phase,
  percent,
}: {
  variant: ImageModeChatVariant;
  detail: Pick<ProjectDetailResponse, "task">;
  slides: ExistingPptChatSlide[];
  phase: "planning" | "awaiting_credits" | "queued" | "rendering" | "complete" | "failed" | "cancelled";
  percent: number;
}): ExistingPptGenerationMessage[] {
  const total = Math.max(1, slides.length);
  const completedSlides = slides.filter((slide) => slide.status === "succeeded");
  const completed = completedSlides.length;
  const running = slides.find((slide) => slide.status === "running");
  const existingPpt = variant === "existing_ppt_beautify";
  const intro = existingPpt
    ? `已收到“${detail.task.title}”，将保持原 PPT 的 ${total} 页页序和逐页内容边界进行美化。`
    : `已收到“${detail.task.title}”，将根据已确认的大纲与视觉风格逐页生成 ${total} 页图片型 PPT。`;
  const completedSummary = completed > 0
    ? `已完成 ${completed}/${total} 页：${completedSlides.map((slide) => `第 ${slide.index} 页「${slide.title}」`).join("、")}。`
    : `已完成 0/${total} 页。`;
  if (phase === "complete") {
    return [{
      id: "generation",
      tone: "success",
      text: `${intro}\n${completedSummary}\n${existingPpt ? "美化完成" : "图片模式生成完成"}，共生成 ${total} 页。现在可以选择任意页面并发送修改或重新生成要求。`,
    }];
  }
  const currentProgress = running
    ? `正在生成第 ${running.index} 页，整体进度 ${percent}%。`
    : detail.task.progressText || `正在准备逐页生成，整体进度 ${percent}%。`;
  const generationCard: ExistingPptGenerationMessage = {
    id: "generation",
    tone: phase === "failed" || phase === "cancelled" ? "info" : "progress",
    text: `${intro}\n${completedSummary}\n${currentProgress}`,
  };
  if (phase === "failed") {
    return [
      generationCard,
      { id: "failed", tone: "error", text: detail.task.errorMessage || detail.task.progressText || "生成未完成，请重新生成。" },
    ];
  }
  if (phase === "cancelled") {
    return [
      generationCard,
      { id: "cancelled", tone: "error", text: "生成已停止，现有项目内容和已完成页面仍会保留。" },
    ];
  }
  return [generationCard];
}

function formatMessageTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function AnimatedEllipsis() {
  return <span className="animated-ellipsis" aria-label="生成中">...</span>;
}

function trimTerminalPunctuation(value: string): string {
  return value.replace(/[。.!！…\s]+$/u, "");
}
